import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { canShowAdmin } from "services/categories";
import { signOutFirebase } from "services/firebase";
import { getContractStatus } from "services/contracts";
import SessionLoading from "components/SessionLoading";
import ContractAcceptance from "./Acceptance";

export default function ContractAccessGate({ user, children }) {
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const exempt = !user || user.access?.active === false || canShowAdmin(user);
  useEffect(() => {
    if (exempt) return undefined;
    let active = true;
    setState(null);
    setError("");
    const check = async () => {
      try {
        const result = await getContractStatus();
        if (active) {
          setState({ ...result, uid: user.uid });
          setError("");
        }
      } catch (failure) {
        if (active) {
          setState(null);
          setError(failure.message);
        }
      }
    };
    const invalidated = () => {
      setState(null);
      check();
    };
    check();
    window.addEventListener("focus", check);
    window.addEventListener("pso:contract-accepted", check);
    window.addEventListener("pso:contract-required", invalidated);
    const timer = setInterval(check, 60000);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", check);
      window.removeEventListener("pso:contract-accepted", check);
      window.removeEventListener("pso:contract-required", invalidated);
    };
  }, [user?.uid, exempt, revision]);
  const blocked =
    !exempt && !(state?.uid === user.uid && state.accepted) && pathname !== "/contrato";
  const reload = () => {
    setState(null);
    setError("");
    setRevision((value) => value + 1);
  };
  if (!exempt && (state?.uid !== user.uid || error))
    return (
      <SessionLoading
        error={error}
        onRetry={reload}
        onSignOut={() => signOutFirebase().catch((failure) => setError(failure.message))}
      />
    );
  return (
    <>
      {!blocked && children}
      <Dialog
        open={blocked}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
        aria-labelledby="contract-dialog-title"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        BackdropProps={{ sx: { bgcolor: "rgba(20, 30, 23, 0.65)" } }}
      >
        <DialogTitle id="contract-dialog-title" sx={{ bgcolor: "#138735", color: "#fff", p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Icon>description</Icon>
            <Typography variant="h5" color="inherit">
              Contrato de uso da plataforma
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: "24px !important" }}>
          {error ? (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>
              <Button onClick={reload}>Tentar novamente</Button>
              <Button
                onClick={() => signOutFirebase().catch((failure) => setError(failure.message))}
              >
                Sair da conta
              </Button>
            </Stack>
          ) : state?.contract ? (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Para continuar usando o Publique Sua Obra, leia o contrato e confirme seu aceite.
              </Typography>
              <Box sx={{ p: 2, border: "1px solid #e0e8e2", borderRadius: 2 }}>
                <Typography variant="h6">
                  {state.contract.isDemo ? "Contrato de uso da plataforma" : state.contract.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Versão {state.contract.versionId}
                </Typography>
                <Typography variant="body2" mt={1}>
                  Você poderá consultar o documento novamente na opção Contrato do menu.
                </Typography>
              </Box>
              <Button
                component={user.access && !user.access.functions.includes("contract") ? "a" : Link}
                to={
                  user.access && !user.access.functions.includes("contract")
                    ? undefined
                    : "/contrato"
                }
                href={
                  user.access && !user.access.functions.includes("contract")
                    ? state.contract.pdfUrl
                    : undefined
                }
                target={
                  user.access && !user.access.functions.includes("contract") ? "_blank" : undefined
                }
                rel="noopener noreferrer"
                variant="outlined"
                color="success"
                endIcon={<Icon>arrow_forward</Icon>}
              >
                Ler contrato
              </Button>
              <ContractAcceptance
                key={state.contract.versionId}
                contract={state.contract}
                onAccepted={reload}
                onReload={reload}
              />
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={2}>
              <CircularProgress aria-label="Consultando contrato" />
              <Typography variant="body2">Consultando o contrato...</Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
